import React from 'react'
import { CurrencyAmount, FormatCurrencyValue, formatCurrencyValue } from '../utils/currency';
import { LineItem } from '../components/LineItems/LineItem';
import { ForSaleConfiguration, LineItemTypes, ShirtData } from '../components/LineItems/LineItemTypes/LineItemTypes';
import moment from 'moment';
import { CardInput } from '../utils/kit/modules/cardDetails';
import { getPublicKey, ProcessPayment, singularPollForStatus } from '../utils/kit/modules/paymentProcessing';
import useIntervalTimer from './useIntervalTimer';
import { getCartId } from '../functions/Items';

/*
Parameters to provide to the processPayment function
Price map: map of lineItem ids and prices
Data map: map of lineItem ids and data
line items: line items for order
card data: Card Information
onPaymentSuccess: Function to call if payment is successful
onPaymentFailure: Function to call if payment fails.

NOTE: re-use of this multiple times (to process payments in bulk) is untested.
*/
export interface processPaymentInput {
    priceMap: Record<number, CurrencyAmount>,
    dataMap: Record<number, any>,
    lineItems: LineItem[],
    cardData?: CardInput,
    onPaymentSuccess: () => void,
    onPaymentFailure: () => void
}

/*
    Payment Processing Statuses:
    AWAITING_INPUT: Initial State. Awaiting processPayment to be called.
    BUILDING_ORDER: processPayment has been called, and this hook is currently transforming the input parameters
    to send to the backend. (State Buffering might cause this not to be displayed.)
    PROCESSING_PAYMENT: The order has begun to be sent to the backend. Once the order has been sent,
    the state will remain here as the hook automatically polls for success.
    PAYMENT_SUCCESSFUL: The backend has returned an affirmative response, confirming the order has been processed.
    PAYMENT_PROBLEM: The backend has returned an error response.
    (TODO: This only occurs during the 'normal' processing loop. An error in extended processing should also trigger this.)
    PROCESSING_PAYMENT_EXTENDED: The order has been pending for an extended period. The rate of polling for the result
    is now slowed down, and the time until the next poll is provided.
    PAYMENT_FAILURE: After the extended processing threshold has been reached, an error or timeout state occurs.
    (TODO: This should only be timeout.)
*/
export enum paymentProcessingStatuses {
    AWAITING_INPUT = 1,
    BUILDING_ORDER = 2,
    PROCESSING_PAYMENT = 3,
    PAYMENT_SUCCESSFUL = 4,
    PAYMENT_PROBLEM = 5,
    PROCESSING_PAYMENT_EXTENDED = 6,
    PAYMENT_FAILURE = 7
}

/* 
Custom Hook to process Payments
Hook Inputs:
userTGAId: the TGA ID to use for payment encryption/decryption.
accessToken: Access token for user.
*/
const usePaymentProcessing = (userTGAId: number, accessToken: string) => {

    const [processingError, setProcessingError] = React.useState<string>();
    const [pendingOrder, setPendingOrder] = React.useState<any>();
    const [completedOrderId, setCompletedOrderId] = React.useState<string>("");
    const [pollPaymentID, setPollPaymentID] = React.useState<string>("");
    const [messageCount, setMessageCount] = React.useState<number>(0);
    const [paymentProcessingStatus, setPaymentProcessingStatus] = React.useState<paymentProcessingStatuses>(paymentProcessingStatuses.AWAITING_INPUT)

    const maxTimeoutRetries : number = Number(process.env.REACT_APP_PAYMENT_PROCESSING_TIMEOUT_RETRIES);

    //These are refs instead of states to avoid re-renders (TODO: Could be refactored out?)

    const successRef = React.useRef<() => void>(() => {});
    const failureRef = React.useRef<() => void>(() => {});
    const stopTimerRef = React.useRef<() => void>(() => {});


    const onProcessingDelayExpiration = React.useCallback(() => {
        
        if (messageCount < maxTimeoutRetries) {
            setMessageCount(prevCount => prevCount + 1);
            const promisePollStatus = singularPollForStatus(pollPaymentID, accessToken);
            promisePollStatus.then((res) => {
                if (res.status === "success") {
                    res.orderId && setCompletedOrderId(res.orderId);
                    setPaymentProcessingStatus(paymentProcessingStatuses.PAYMENT_SUCCESSFUL);
                    stopTimerRef.current();
                    successRef.current();
                } else if (res.status === 'pending') {
                    console.log(`PENDING: ${messageCount}`);
                } else if (res.status === "failure") {
                    setMessageCount(maxTimeoutRetries + 1);
                }
            });
        } else {
            setProcessingError(`There was a problem with your payment. Please verify your information and try again.`);
            stopTimerRef.current();
            setMessageCount(0);
            setPaymentProcessingStatus(paymentProcessingStatuses.PAYMENT_FAILURE);
            failureRef.current();
        }
    }, []);

    const {startTimer: startProcessingTimer, stopTimer: stopProcessingTimer, formattedTimeRemaining: formattedProcessingTimeRemaining, timerStatus: timerProcessingStatus} = useIntervalTimer(true, onProcessingDelayExpiration);

    stopTimerRef.current = stopProcessingTimer;





    const processPayment = React.useCallback(async (input: processPaymentInput) => {
        setProcessingError(undefined);
        setPaymentProcessingStatus(paymentProcessingStatuses.BUILDING_ORDER);
        const lineItemData = constructLineItemData(input.priceMap, input.dataMap, input.lineItems);

        const order = {
            timestamp: moment().utc().format(),
            currencyCode: 840,
            lineItems: lineItemData,
            totalCost: getTotalofPriceMap(input.priceMap).string ?? "0.00",
            paymentMethod: input.cardData,
            cartId: getCartId()
        };
        
        const freshPubKey = await getPublicKey(userTGAId);
        setPendingOrder(order);

        setPaymentProcessingStatus(paymentProcessingStatuses.PROCESSING_PAYMENT);
        const requestStatus = await ProcessPayment(order, freshPubKey, accessToken);

        if (requestStatus.status === 'success' && requestStatus.pollState !== undefined) {
            requestStatus.pollState.then((res) => {
                if (res.status === 'success') {
                    res.orderId && setCompletedOrderId(res.orderId);
                    setPaymentProcessingStatus(paymentProcessingStatuses.PAYMENT_SUCCESSFUL);
                    input.onPaymentSuccess();
                } else if (res.status === 'failure') {
                    setPaymentProcessingStatus(paymentProcessingStatuses.PAYMENT_PROBLEM);
                    setProcessingError(`Please verify your information and try again. If you need assistance, please contact us and refer to payment id ${requestStatus.payment_id}`);
                } else if (res.status === 'failure_timeout') {
                    if (requestStatus.payment_id) {
                        setPollPaymentID(requestStatus.payment_id);
                        setProcessingError(`Your payment is taking a long time to process. Please wait. If you need assistance, please contact us and refer to payment id ${requestStatus.payment_id}`);
                        startProcessingTimer(300);
                        setPaymentProcessingStatus(paymentProcessingStatuses.PROCESSING_PAYMENT_EXTENDED);
                        successRef.current = input.onPaymentSuccess;
                        failureRef.current = input.onPaymentFailure;
                    } else {
                        setPaymentProcessingStatus(paymentProcessingStatuses.PAYMENT_PROBLEM);
                        setProcessingError(`There was a problem with your payment. Please verify your information and try again.`);
                    }
                }
            });
        } else {
            setPaymentProcessingStatus(paymentProcessingStatuses.PAYMENT_PROBLEM);
            setProcessingError(requestStatus.reason ?? 'An unexpected error occurred with your payment. Please try again.');
        }
    }, [userTGAId, accessToken, startProcessingTimer]);





    const getTotalofPriceMap = (priceMap: Record<string, CurrencyAmount>) : FormatCurrencyValue => {
    
        const currency = Object.values(priceMap)[0].currency;

        const sumTotal = Object.values(priceMap)
            .map((amount) => amount.value)
            .reduce(((acc, value) => 
            {
                return(acc+((value?.float !== undefined) ? value.float : 0))
            }),0);

        return(formatCurrencyValue(sumTotal, currency.formatting));
    }

    function constructLineItemData(priceMap: Record<number, CurrencyAmount>, dataMap: Record<number, any>, lineItems: LineItem[]) {
        return Object.entries(priceMap).filter(([id, price]) => price.value.float !== 0).map(([id, price]) => {

            const lineItem = lineItems?.find((item) => item.itemConfiguration.dbId === Number(id));
            const lineItemType = lineItem?.itemFormat;

            if(lineItemType === LineItemTypes.Donation)
            {
                return(
                {
                    name: lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.name ?? "",
                    dbId: lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.itemConfiguration.dbId ?? 0,
                    price: 
                    {
                        qty: 1,
                        unitPrice: price.value.string ?? "0.00",
                        totalPrice: price.value.string ?? "0.00"
                    },
                    data: dataMap[lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.itemConfiguration.dbId ?? 0],
                })
            }
            if(lineItemType === LineItemTypes.ForSale)
            {
                const data = dataMap[lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.itemConfiguration.dbId ?? 0]
                const quantity = data.registrationAttributions.length;
                const unitPrice = (lineItem?.itemConfiguration as ForSaleConfiguration).unitPrice
                return(
                {
                    name: lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.name ?? "",
                    dbId: lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.itemConfiguration.dbId ?? 0,
                    price: 
                    {
                        qty: quantity ?? 0,
                        unitPrice: unitPrice.value.string ?? "0.00",
                        totalPrice: price.value.string ?? "0.00"
                    },
                    data: data,
                })
            }
            if(lineItemType === LineItemTypes.Shirts)
            {
                const data = dataMap[lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.itemConfiguration.dbId ?? 0] as ShirtData;
                const dataClean = {
                    ...data,
                    dbId: Number(id),
                    sizes: data.sizes.map((size) => 
                    {
                        return {
                            ...size,
                            unitPrice: size.unitPrice.value.string
                        }
                    })
                }
                const quantity = data.totalQuantity;
                const currency = Object.values(priceMap)[0].currency;
                const totalPriceUnformat: number = data.sizes.reduce((acc, size) => 
                {
                    return acc + (size.quantity * (size.unitPrice.value.float ?? 0));
                },0 as number);
                const totalPrice = formatCurrencyValue(totalPriceUnformat, currency.formatting)
                //TODO: Talk with ed on how to implement this
                const unitPrice = quantity !== 0 ? formatCurrencyValue((totalPrice.float ?? 0) / quantity, currency.formatting).string : "0.00";
                return(
                {
                    name: lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.name ?? "",
                    dbId: lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.itemConfiguration.dbId ?? 0,
                    price: 
                    {
                        qty: quantity ?? 0,
                        unitPrice: unitPrice ?? "0.00",
                        totalPrice: totalPrice.string ?? "0.00"
                    },
                    data: dataClean,
                })
            }
            else
            {
                //Set empty item for now
                return(
                {
                    name: lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.name ?? "",
                    dbId: lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.itemConfiguration.dbId ?? 0,
                    price: 
                    {
                        qty: 1,
                        unitPrice: price.value.string ?? "0.00",
                        totalPrice: price.value.string ?? "0.00"
                    },
                    data: dataMap[lineItems?.find((item) => item.itemConfiguration.dbId === Number(id))?.itemConfiguration.dbId ?? 0],
                })
            }
        })
    }

    
    const payRequestProcessing = React.useMemo(() => {
        return [
            paymentProcessingStatuses.BUILDING_ORDER, 
            paymentProcessingStatuses.PROCESSING_PAYMENT, 
            paymentProcessingStatuses.PROCESSING_PAYMENT_EXTENDED
        ].includes(paymentProcessingStatus);
    }, [paymentProcessingStatus]);

    return React.useMemo(() => ({
        processingError: processingError,
        payRequestProcessing: payRequestProcessing,
        completedOrderId: completedOrderId,
        completedOrder: paymentProcessingStatus === paymentProcessingStatuses.PAYMENT_SUCCESSFUL ? pendingOrder : undefined,
        checkoutComplete: paymentProcessingStatus === paymentProcessingStatuses.PAYMENT_SUCCESSFUL,
        formattedExtendedProcessingTimeRemaining: formattedProcessingTimeRemaining,
        extendedProcessingTimerStatus: timerProcessingStatus,
        extendedProcessingMessage: `We will check the status of your payment in ${formattedProcessingTimeRemaining}.`,
        processingDelay: paymentProcessingStatus === paymentProcessingStatuses.PROCESSING_PAYMENT_EXTENDED,
        paymentProcessingStatus: paymentProcessingStatus,
        processPayment: processPayment
    }), [
        processingError, payRequestProcessing, completedOrderId, paymentProcessingStatus, 
        pendingOrder, formattedProcessingTimeRemaining, timerProcessingStatus, processPayment
    ]);

}

export default usePaymentProcessing;
